import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, signal } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community'; // GridReadyEvent used in gridOptions callback
import { GridPersist } from '../grid/grid-persist';
import { VoucherCreditDto } from '../services/api.service';

@Component({
  selector: 'cert-voucher-credits-grid',
  standalone: false,
  templateUrl: './voucher-credits-grid.component.html',
})
export class VoucherCreditsGridComponent {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  @Input() credits: VoucherCreditDto[] = [];
  @Output() update = new EventEmitter<number[]>();
  @Output() delete = new EventEmitter<number[]>();

  protected gridApi!: GridApi;
  protected errorEmitter = new EventEmitter<any>();
  selectedCreditIds = signal<number[]>([]);

  protected colDefs: ColDef<VoucherCreditDto>[] = [
    {
      field: 'voucherCreditAssignmentId',
      headerName: '',
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      minWidth: 50,
      maxWidth: 50,
      hide: true,
    },
    {
      field: 'personName',
      headerName: 'Person',
    },
    {
      field: 'ein',
      headerName: 'EIN',
    },
    {
      field: 'serviceCodeDescription',
      headerName: 'Service',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) =>
        params.value != null
          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value)
          : '',
    },
    {
      field: 'expirationDate',
      headerName: 'Expires',
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => this.datePipe.transform(params.value, 'M/d/yyyy') || '',
    },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: (params: any) => {
        const cssClass = this.getStatusClass(params.data);
        return `<span class="badge badge-md ${cssClass}">${params.value ?? ''}</span>`;
      },
    },
  ];

  gridPersist = new GridPersist('cert-voucher-credits-grid');

  protected gridOptions: GridOptions<VoucherCreditDto> = this.gridPersist.setup({
    domLayout: 'autoHeight',

    rowSelection: { mode: 'multiRow', enableClickSelection: false, checkboxes: true },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [10, 25, 50, 100],
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
    },
    columnDefs: this.colDefs,
    onSelectionChanged: () => {
      this.selectedCreditIds.set(this.gridApi?.getSelectedRows().map((r) => r.voucherCreditAssignmentId) ?? []);
    },
    onGridReady: (params: GridReadyEvent<VoucherCreditDto>) => {
      this.gridApi = params.api;
    },
    onGridSizeChanged: () => this.gridApi?.sizeColumnsToFit(),
  });

  constructor(private readonly datePipe: DatePipe) {}

  onUpdate() {
    if (this.selectedCreditIds().length === 0) return;
    this.update.emit([...this.selectedCreditIds()]);
  }

  onDelete() {
    if (this.selectedCreditIds().length === 0) return;
    this.delete.emit([...this.selectedCreditIds()]);
  }

  clearSelection() {
    this.gridApi?.deselectAll();
  }

  resetFilters() {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.gridPersist.resetAndSaveGrid(this.gridApi);
    }
  }

  private getStatusClass(credit: VoucherCreditDto): string {
    if (credit.isRedeemed) return 'badge-success';
    if (credit.status === 'Expired') return 'badge-danger';
    return 'badge-info';
  }
}
